import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { Formik, FormikHelpers } from 'formik';
import { FetchResult } from '@apollo/client';

// Data
import { ICreateDrillContainer, ICreateDrillFields, ISkillSetFields } from './create-drill.interface';

// Components
import { CreateDrill } from './create-drill.component';
import { toServerSkillSets } from './create-drill.helpers';
import { useCompanySportTerminology } from '../../../lib/stores/company.store';
import { SportTerminology } from '../../../lib/types';

export const CreateDrillContainer: React.FC<ICreateDrillContainer> = ({
  drill,
  user,
  createDrill,
  checkConformity,
  skills,
  positions,
  loading,
  fetchError
}) => {
  const termLookup = useCompanySportTerminology();

  const [submitError, setSubmitError] = useState<string | null>();
  const [showSaved, setShowSaved] = useState(false);
  const [danglingVideoFilenames, setDanglingVideoFilenames] = useState<string[]>([]);
  const [skillSets, setSkillSets] = useState<ISkillSetFields[]>([{ positionIds: [], skillIds: [] }]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (drill) {
      const nextSkillSets: ISkillSetFields[] = [];

      _.each(drill.skillSets, skillSet => {
        const currPositions: IPosition[] = [];
        const currSkills: ISkill[] = [];

        _.each(skillSet.positionIds, _id => {
          const foundPosition = _.find(positions, position => position._id === _id);
          if (foundPosition) currPositions.push(foundPosition);
        });

        _.each(skillSet.skillIds, _id => {
          const foundSkill = _.find(skills, skill => skill._id === _id);
          if (foundSkill) currSkills.push(foundSkill);
        });

        nextSkillSets.push({
          skillIds: _.map(currSkills, currSkill => ({ value: currSkill._id, label: currSkill.name })),
          positionIds: _.map(currPositions, currPosition => ({ value: currPosition._id, label: currPosition.name })),
        });
      });

      nextSkillSets.push({ positionIds: [], skillIds: [] })
      setSkillSets(nextSkillSets);
    }
  }, [drill, positions.length, skills.length]);

  const INITIAL_FORM_VALUES: ICreateDrillFields = {
    drillName: drill ? drill.name : '',
    skillSets,
    isGlobal: false
  };

  async function handleSubmit(values: ICreateDrillFields, {setSubmitting, resetForm}: FormikHelpers<ICreateDrillFields>) {
    setSubmitError(null);
    setDanglingVideoFilenames([]);
    const skillSets: ISkillSet[] = toServerSkillSets(values.skillSets);

    // Best-effort, edit-only preview of which calibration videos these skillSets orphan; the
    // try/catch keeps a failed preview from ever blocking the save (see "non-blocking" rule).
    let dangling: string[] = [];
    try {
      dangling = await checkConformity(skillSets);
    } catch (err) {
      console.error('checkConformity failed (preview skipped):', err);
    }

    createDrill({
      name: values.drillName,
      skillSets,
      companyId: values.isGlobal ? null : user?.settings.activeCompanyId
    })
      .then((result: FetchResult) => {
        setSubmitting(false);

        if (result.errors && result.errors.length) {
          console.error('createDrill mutation errors:', result.errors);
          setSubmitError(`Couldn't save the ${termLookup[SportTerminology.EVENT].toLowerCase()}. Please try again.`);
        } else {
          setDanglingVideoFilenames(dangling);
          setShowSaved(true);
          setTimeout(() => {
            setShowSaved(false);
          }, 2000);

          resetForm({ values: INITIAL_FORM_VALUES })
        }
      })
      .catch((err) => {
        console.error('createDrill failed:', err);
        setSubmitError(`Couldn't save the ${termLookup[SportTerminology.EVENT].toLowerCase()}. Please try again.`);
      })
  }

  const validationSchema = Yup.object().shape({
    drillName: Yup.string().required(`Enter a ${termLookup[SportTerminology.EVENT].toLowerCase()} name`),
  });

  const skillOptions = skills.map(skill => ({ value: skill._id, label: skill.name }));
  const positionOptions = positions.map(position => ({ value: position._id, label: position.name, active: !position.deleted }))

  return (
    <Formik
      enableReinitialize
      initialValues={INITIAL_FORM_VALUES}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {form => <CreateDrill
        form={form}
        checkConformity={checkConformity}
        submitError={submitError ?? undefined}
        showSaved={showSaved}
        danglingVideoFilenames={danglingVideoFilenames}
        skillOptions={skillOptions}
        positionOptions={positionOptions}
        loading={loading}
        fetchError={fetchError}
        isEditing={Boolean(drill)}
        isAdmin={user?.isAdmin || false}
      />}
    </Formik>
  );
}
